import * as React from "react";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Urls } from "../../../../../Urls";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { OrderSetupReview } from "../../../Common/Review/Component/OrderSetupReview";
import { ITableConfig, Table } from "../../../../../../Shared/Table/Table";
import { Order } from "../../../../../../../Wrap/Models/Domains/Orders/Order";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { SellAction } from "../../Action/SellAction";
import { BatchInfoModel } from "../../../../../../../Wrap/Models/Domains/Orders/Batch/BatchInfoModel";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { equlityBondWarning, hasDecisionMaker } from "../../../Switch/Resource/Content";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { CommissionChargeCancelMsg } from "../../../Common/AdviserCharges/Components/CommissionChargeCancelMsg";
import { OrderStatus } from "../../../../../../../Wrap/Constants/Enums/Domain/OrderStatus";

interface IReviewProps extends IProcessComponentProps {
    type: SubAccountType;
    onClearError: VoidFunction;
}

interface IReviewState {
    investmentList: Array<Order>;
}

export class Review extends ProcessComponent<IReviewProps, IReviewState> {
    constructor(props: any) {
        super(props);
        this.state = {
            investmentList: [],
        };
    }

    private navigate() {
        this.props.onClearError();
        Navigator.navigateTo(Urls.get().SellInvestmentSelection, {
            scrollAfterNavigating: true,
            queryParamsObject: {
                claccountid: getQueryParameters()["claccountid"],
                hierarchyid: getQueryParameters()["hierarchyid"],
                subaccounthierarchyid: getQueryParameters()["subaccounthierarchyid"],
                batchId: this.props.data.BatchId,
            },
        });
    }

    private getTableColumn() {
        const tableColConfig: ITableConfig[] = [
            {
                title: "Investment",
                key: "Description",
                render: (data, row: Order, index) => {
                    return row.Description;
                },
                thCssWrapper: "cell-left",
                tdCssWrapper: "cell-left",
            },
            {
                title: "Sell value",
                key: "OrderValue",
                render: (data, row: Order, index) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(Math.abs(row.OrderValue), CurrencyCode.GBP, true, 2);
                },
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
            {
                title: "Sell quantity",
                key: "OrderQuantity",
                render: (data, row: Order, index) => {
                    if (row?.SecurityType == "Model") { return null; }
                    else {
                        return MoneyDisplayHelper.FromatMoneyDisplay(row.OrderQuantity, null, false, 4);
                    }
                },
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },

            {
                title: "Limit price",
                key: "OrderLimit",
                render: (data, row: Order, index) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(row.OrderLimit, null, false, 2);
                },
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
            {
                title: "Status",
                key: "OrderStatus",
                render: (data, row: Order, index) => {
                    return row.OrderStatus;
                },
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
        ]; // all table column config

        return tableColConfig;
    }

    componentDidMount() {
        SellAction.getBatchInfo(this.props.data, (callBack: BatchInfoModel) => {
            this.setState({
                investmentList: callBack.InvestmentList,
            });
        });
    }

    public render(): JSX.Element {
        const { data, type, onChange } = this.props;
        const investmentList = this.state.investmentList;
        const orderList = investmentList.filter((o) => o.Status === OrderStatus.Created);
        return (
            <>
                {hasDecisionMaker(data.InvestmentList) && (
                    <Alert alertType={AlertType.Warning} message={equlityBondWarning} />
                )}
                <h2 className="mb-18">
                    <span>Review</span>
                </h2>
                <CommissionChargeCancelMsg processType={ProcessType.Sell} />
                <div className="d-flex flex-space">
                    <h2 className="mb-11">Order setup</h2>
                    <button
                        className="edit-circle-icon-button label-sr-only"
                        title="Edit"
                        onClick={() => {
                            this.props.onClearError();
                            Navigator.navigateTo(Urls.get().SellOrderSetup, {
                                scrollAfterNavigating: true,
                                queryParamsObject: {
                                    claccountid: getQueryParameters()["claccountid"],
                                    hierarchyid: getQueryParameters()["hierarchyid"],
                                    subaccounthierarchyid: getQueryParameters()["subaccounthierarchyid"],
                                    batchId: this.props.data.BatchId,
                                },
                            });
                        }}
                    >
                        <span className="icon" aria-hidden="true"></span>
                        <span className="text">Edit</span>
                    </button>
                </div>
                <hr className="heading-underlined-text"></hr>
                <OrderSetupReview process={ProcessType.Sell} subAccountType={type} data={data} colNum={2} />

                <div className="d-flex flex-space">
                    <h2 className="mb-11">Sell investments</h2>
                    <button
                        className="edit-circle-icon-button label-sr-only"
                        title="Edit"
                        onClick={() => this.navigate()}
                    >
                        <span className="icon" aria-hidden="true"></span>
                        <span className="text">Edit</span>
                    </button>
                </div>
                <hr className="heading-underlined-text"></hr>
                <Table
                    data={orderList}
                    cssWrapper="no-sided-padding no-borders bg-default"
                    col={this.getTableColumn()}
                    TotalBody={[
                        { data: "Estimated net order value", colSpan: 1 },
                        {
                            data: MoneyDisplayHelper.FromatMoneyDisplay(
                                this.state.investmentList.reduce((total, ele) => {
                                    return total + Math.abs(ele.OrderValue);
                                }, 0),
                                CurrencyCode.GBP,
                                true,
                                2
                            ),
                            tfTdCssWrapper: "cell-right pr-12",
                            colSpan: 0,
                        },
                    ]}
                />
                <hr />
                <div className="no-print">
                    <CheckboxEditor
                        value={data.AdviceGiven}
                        onChange={(v) => {
                            this.props.onChange({ AdviceGiven: v });
                        }}
                        label="Please indicate if advice was given on this trade."
                        cssWrapper="square-checkbox block"
                    ></CheckboxEditor>
                </div>
            </>
        );
    }
}
